/**
 * P0 Scheduling Calendar — Phase 2C (Multi-view + Multi-session chips)
 * ─────────────────────────────────────────────────────────────
 * Contractor swim-lane grid extended with:
 *   • 4-window selector (Week · Month · Quarter · Year — Rev 3 Q10)
 *   • 4-view toggle    (Planned · Actual · Reconciled · Overlay — Rev 3 Q10/Q11)
 *   • Multi-session chip rendering — one segmented chip per session,
 *     cure-gap sessions rendered dashed violet (Rev 3 §4.7)
 *   • Session drawer (per-session edit) — bumps plan_version and
 *     writes a fresh schedule_events_plan_history row
 *
 * Reconciliation data pulled from `/api/schedule/reconcile-window` +
 * `/api/schedule/events/{id}/reconcile`. Consumes shipped
 * services/reconciliation.py.
 *
 * Design: Tactical Industrial (matches DashboardOverviewPage).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AvailabilityPickerDrawer from '@/components/AvailabilityPickerDrawer';
import {
  CalendarBlank, CaretLeft, CaretRight, Hammer, Wrench, Eye,
  Broom, X, CloudRain, UsersThree, Package, ArrowsClockwise,
  Warning, ChartLineUp, Stack, ClockClockwise, Trash, Plus,
  Sun, Cloud, Wind, Snowflake, Thermometer,
} from '@phosphor-icons/react';

const EVENT_TYPE_COLOUR = {
  install:     { bg: 'bg-blue-50',    fg: 'text-blue-900',    br: 'border-blue-400',    icon: Hammer },
  strip:       { bg: 'bg-amber-50',   fg: 'text-amber-900',   br: 'border-amber-500',   icon: Broom },
  inspection:  { bg: 'bg-violet-50',  fg: 'text-violet-900',  br: 'border-violet-500',  icon: Eye },
  maintenance: { bg: 'bg-emerald-50', fg: 'text-emerald-900', br: 'border-emerald-500', icon: Wrench },
  repair:      { bg: 'bg-orange-50',  fg: 'text-orange-900',  br: 'border-orange-500',  icon: Wrench },
  other:       { bg: 'bg-slate-100',  fg: 'text-slate-800',   br: 'border-slate-500',   icon: Package },
};

const STATUS_ACCENT = {
  tentative: 'border-dashed',
  confirmed: 'border-solid',
  completed: 'opacity-70 border-solid',
  cancelled: 'hidden',
};

// Session-role styles (Rev 3 §4.7 · seven states).
const SESSION_ROLE_STYLE = {
  cure_gap: { bg: 'bg-violet-100', fg: 'text-violet-900', br: 'border-violet-500', dashed: true },
};

const SESSION_STATUS_TINT = {
  complete: 'bg-emerald-100',
  partial:  'bg-amber-100',
  skipped:  'bg-slate-200 line-through opacity-60',
  cure_active: 'bg-violet-100',
  cure_elapsed: 'bg-violet-200',
};

const WINDOW_OPTIONS = [
  { key: 'week',    label: 'Week',    weeks: 1 },
  { key: 'month',   label: 'Month',   weeks: 4 },
  { key: 'quarter', label: 'Quarter', weeks: 12 },
  { key: 'year',    label: 'Year',    weeks: 52 },
];

const VIEW_OPTIONS = [
  { key: 'planned',    label: 'Planned',    icon: Stack },
  { key: 'actual',     label: 'Actual',     icon: ClockClockwise },
  { key: 'reconciled', label: 'Reconciled', icon: ChartLineUp },
  { key: 'overlay',    label: 'Overlay',    icon: Warning },
];

const SESSION_ROLES = [
  'prep','strip','install','bed','point','seal','coat','rescrew','rust','wash',
  'cure_gap','cleanup','qc_inspect','followup','inspect',
];
const CURE_REASONS = [
  'mortar_cure_7d','mortar_cure_2wk','mortar_cure_pointing_3h',
  'paint_recoat_window','sealer_dry_24h','butynol_cure_48h','other',
];

const fmtDayShort = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-NZ', { weekday: 'short' });
};
const fmtDayNum = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}/${d.getMonth() + 1}`;
};
const isWeekend = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.getDay() === 0 || d.getDay() === 6;
};

const mondayOf = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const shiftWeeks = (iso, weeks) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
};

const dateInRange = (iso, startIso, endIso) =>
  iso >= startIso && iso <= (endIso || startIso);

export default function SchedulePage() {
  const { api } = useAuth();
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [windowKey, setWindowKey] = useState('month');
  const [view, setView] = useState('planned');
  const [showWeekends, setShowWeekends] = useState(false);
  const [data, setData] = useState(null);
  const [reconMap, setReconMap] = useState({});
  const [weatherByDate, setWeatherByDate] = useState({});
  const [availabilityFor, setAvailabilityFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState('');

  const weeks = useMemo(
    () => WINDOW_OPTIONS.find(w => w.key === windowKey)?.weeks || 4,
    [windowKey],
  );

  const fetchWindow = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/schedule?week_start=${weekStart}&weeks=${weeks}`);
      setData(r.data);
      // Reconciliation only needed for reconciled + overlay views
      if (view === 'reconciled' || view === 'overlay') {
        const rr = await api.get(
          `/schedule/reconcile-window?week_start=${weekStart}&weeks=${weeks}`,
        );
        setReconMap(rr.data?.events || {});
      } else {
        setReconMap({});
      }
      // Phase 2E · weather badge strip on day headers
      try {
        const wr = await api.get(
          `/schedule/weather/forecast?week_start=${weekStart}&weeks=${weeks}`,
        );
        const map = {};
        for (const d of (wr.data?.forecast || [])) map[d.date] = d;
        setWeatherByDate(map);
      } catch {
        setWeatherByDate({});
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weeks, view, api]);

  useEffect(() => { fetchWindow(); }, [fetchWindow]);

  // ── Drag/drop handlers ────────────────────────────────────────────
  const onDragStartFromPool = (job) => (e) => {
    setDragging({ jobId: job._id, source: 'pool', appraisal: job });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job._id);
  };
  const onDragStartFromGrid = (event) => (e) => {
    setDragging({ eventId: event._id, source: 'grid', event });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', event._id);
  };
  const onDragOverCell = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDropCell = (contractorId, dayIso) => async (e) => {
    e.preventDefault();
    if (!dragging) return;
    try {
      if (dragging.source === 'pool') {
        await api.post('/schedule/events', {
          appraisal_id: dragging.jobId, contractor_id: contractorId,
          start_date: dayIso, end_date: dayIso,
          event_type: 'install', status: 'tentative',
          estimated_hours: 8, crew_size: 1, equipment_required: [], notes: '',
        });
      } else if (dragging.source === 'grid') {
        await api.patch(`/schedule/events/${dragging.eventId}`, {
          contractor_id: contractorId, start_date: dayIso,
        });
      }
      await fetchWindow();
    } catch (ex) {
      setError(`Assign failed: ${ex?.response?.data?.detail || ex.message}`);
    } finally {
      setDragging(null);
    }
  };
  const onDropPool = async (e) => {
    e.preventDefault();
    if (!dragging || dragging.source !== 'grid') return;
    try {
      await api.delete(`/schedule/events/${dragging.eventId}`);
      await fetchWindow();
    } catch (ex) {
      setError(`Unassign failed: ${ex?.response?.data?.detail || ex.message}`);
    } finally {
      setDragging(null);
    }
  };

  const updateSelectedEvent = async (patch) => {
    if (!selectedEvent) return;
    try {
      const r = await api.patch(`/schedule/events/${selectedEvent._id}`, patch);
      setSelectedEvent({ ...selectedEvent, ...r.data });
      await fetchWindow();
    } catch (ex) {
      setError(`Update failed: ${ex?.response?.data?.detail || ex.message}`);
    }
  };

  const replaceSelectedSessions = async (sessions) => {
    if (!selectedEvent) return;
    try {
      const r = await api.put(
        `/schedule/events/${selectedEvent._id}/sessions`,
        { sessions },
      );
      setSelectedEvent({ ...selectedEvent, ...r.data });
      await fetchWindow();
    } catch (ex) {
      setError(`Sessions save failed: ${ex?.response?.data?.detail || ex.message}`);
    }
  };

  const deleteSelectedEvent = async () => {
    if (!selectedEvent) return;
    try {
      await api.delete(`/schedule/events/${selectedEvent._id}`);
      setSelectedEvent(null);
      await fetchWindow();
    } catch (ex) {
      setError(`Delete failed: ${ex?.response?.data?.detail || ex.message}`);
    }
  };

  const visibleDays = useMemo(() => {
    if (!data) return [];
    return showWeekends
      ? data.window_days
      : data.window_days.filter(d => !isWeekend(d));
  }, [data, showWeekends]);

  const eventsByContractor = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (const e of data.events) {
      if (!map[e.contractor_id]) map[e.contractor_id] = [];
      map[e.contractor_id].push(e);
    }
    return map;
  }, [data]);

  const overbookedByContractor = useMemo(() => {
    if (!data) return {};
    const perCellCounts = {};
    for (const e of data.events) {
      const start = new Date(e.start_date + 'T00:00:00');
      const end = new Date((e.end_date || e.start_date) + 'T00:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = d.toISOString().slice(0, 10);
        const key = `${e.contractor_id}::${iso}`;
        perCellCounts[key] = (perCellCounts[key] || 0) + 1;
      }
    }
    const result = {};
    for (const [key, count] of Object.entries(perCellCounts)) {
      if (count < 2) continue;
      const cid = key.split('::')[0];
      result[cid] = (result[cid] || 0) + 1;
    }
    return result;
  }, [data]);

  // Q11 · overlay + long window → legibility warning
  const overlayLegibilityWarning = view === 'overlay' && (weeks >= 12);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* ── Sticky command header ─────────────────────────────────── */}
      <header
        className="sticky top-0 z-30 bg-slate-900 text-slate-100 border-b-2 border-slate-700 px-6 py-3 flex items-center gap-3 flex-wrap"
        data-testid="schedule-header"
      >
        <CalendarBlank size={20} weight="bold" className="text-emerald-400" />
        <h1 className="text-sm font-bold tracking-[0.2em] uppercase" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Work Booking Grid
        </h1>
        <div className="h-6 w-px bg-slate-700" />
        <div className="flex items-center gap-1">
          <button data-testid="schedule-prev-week"
            onClick={() => setWeekStart(shiftWeeks(weekStart, -weeks))}
            className="p-1.5 hover:bg-slate-800 border border-slate-700">
            <CaretLeft size={14} weight="bold" />
          </button>
          <button data-testid="schedule-today"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-slate-800 border border-slate-700">
            Today
          </button>
          <button data-testid="schedule-next-week"
            onClick={() => setWeekStart(shiftWeeks(weekStart, weeks))}
            className="p-1.5 hover:bg-slate-800 border border-slate-700">
            <CaretRight size={14} weight="bold" />
          </button>
        </div>

        {/* Window selector · Q10 */}
        <div className="flex items-center gap-0" data-testid="schedule-window-selector">
          {WINDOW_OPTIONS.map(opt => (
            <button
              key={opt.key}
              data-testid={`schedule-window-${opt.key}`}
              onClick={() => setWindowKey(opt.key)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider border border-slate-700 ${
                windowKey === opt.key ? 'bg-emerald-500 text-slate-900 font-bold' : 'hover:bg-slate-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* View toggle · Q10/Q11 */}
        <div className="flex items-center gap-0" data-testid="schedule-view-toggle">
          {VIEW_OPTIONS.map(opt => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.key}
                data-testid={`schedule-view-${opt.key}`}
                onClick={() => setView(opt.key)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider border border-slate-700 inline-flex items-center gap-1.5 ${
                  view === opt.key ? 'bg-amber-400 text-slate-900 font-bold' : 'hover:bg-slate-800'
                }`}
                title={opt.label}
              >
                <Icon size={12} weight="bold" />
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="text-xs text-slate-400 tracking-wider"
             style={{ fontFamily: "'JetBrains Mono', monospace" }}
             data-testid="schedule-range">
          {weekStart} → {shiftWeeks(weekStart, weeks)}
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-xs uppercase tracking-wider cursor-pointer">
          <input type="checkbox" checked={showWeekends}
            onChange={(e) => setShowWeekends(e.target.checked)}
            data-testid="schedule-toggle-weekends" />
          Weekends
        </label>
        <button onClick={fetchWindow}
          className="p-1.5 hover:bg-slate-800 border border-slate-700"
          data-testid="schedule-refresh" title="Refresh">
          <ArrowsClockwise size={14} weight="bold" />
        </button>
        <button
          onClick={async () => {
            try {
              await api.post('/schedule/weather/refresh?days=7');
              await fetchWindow();
            } catch (e) {
              setError(e?.response?.data?.detail || 'Weather refresh failed');
            }
          }}
          className="p-1.5 hover:bg-slate-800 border border-slate-700 inline-flex items-center gap-1 text-xs uppercase tracking-wider"
          data-testid="schedule-weather-refresh"
          title="Pull latest 7-day forecast from Open-Meteo">
          <CloudRain size={14} weight="bold" /> Weather
        </button>
      </header>

      {overlayLegibilityWarning && (
        <div className="mx-6 mt-4 p-2 border-l-4 border-amber-500 bg-amber-50 text-amber-900 text-xs flex items-center gap-2"
             data-testid="schedule-overlay-warning">
          <Warning size={14} weight="bold" />
          Overlay legibility is poor at this window width — consider
          <button onClick={() => setView('reconciled')}
                  className="underline font-semibold" data-testid="schedule-overlay-switch-recon">
            switch to Reconciled
          </button>.
        </div>
      )}

      {error && (
        <div className="mx-6 mt-4 p-3 border-l-4 border-rose-500 bg-rose-50 text-rose-900 text-sm"
             data-testid="schedule-error">
          {error} · <button className="underline" onClick={() => setError('')}>dismiss</button>
        </div>
      )}

      <div className="flex gap-4 p-6 items-start">
        {/* ── Unassigned pool ─────────────────────────────────────── */}
        <aside
          className="w-72 flex-shrink-0 bg-white border border-slate-300"
          onDragOver={onDragOverCell}
          onDrop={onDropPool}
          data-testid="schedule-unassigned-pool"
        >
          <div className="px-4 py-3 bg-slate-100 border-b border-slate-300 flex items-center gap-2">
            <UsersThree size={14} weight="bold" className="text-slate-600" />
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
              Unassigned · {data?.unassigned_jobs?.length ?? 0}
            </h2>
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-auto p-2 space-y-2">
            {loading && <div className="text-xs text-slate-500 p-3">Loading…</div>}
            {!loading && data?.unassigned_jobs?.length === 0 && (
              <div className="text-xs text-slate-500 p-3 border border-dashed border-slate-300">
                All accepted jobs are scheduled.
              </div>
            )}
            {data?.unassigned_jobs?.map((job) => (
              <div key={job._id} draggable
                onDragStart={onDragStartFromPool(job)}
                className="border-l-4 border-slate-800 bg-white hover:bg-slate-50 border border-slate-200 p-2 cursor-grab active:cursor-grabbing"
                data-testid={`schedule-job-card-${job._id}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500"
                     style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  A{job.appraisal_no || job._id.slice(-4)} · {job.quote_type || '—'}
                </div>
                <div className="text-sm font-bold text-slate-900 mt-1">
                  {job.client_name || 'Unknown Client'}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {job.property_address || '—'}
                </div>
                <div className="text-[10px] text-slate-600 mt-1 uppercase tracking-wider">
                  {job.appraisal_status}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Grid ────────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 bg-white border border-slate-300 overflow-auto">
          {loading && !data ? (
            <div className="p-8 text-sm text-slate-500">Loading schedule…</div>
          ) : (
            <table className="w-full border-collapse text-sm" data-testid="schedule-grid">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr>
                  <th className="border border-slate-300 px-3 py-2 text-left text-[10px] uppercase tracking-wider text-slate-600 min-w-[160px] sticky left-0 bg-slate-100 z-20">
                    Contractor
                  </th>
                  {visibleDays.map((iso, i) => {
                    const weekBoundary = i > 0 && (new Date(iso + 'T00:00:00').getDay() === 1);
                    const wf = weatherByDate[iso];
                    return (
                      <th key={iso}
                        className={`border border-slate-300 px-1 py-2 text-center text-[10px] uppercase tracking-wider text-slate-600 min-w-[70px] ${weekBoundary ? 'border-l-2 border-l-slate-500' : ''} ${isWeekend(iso) ? 'bg-slate-200' : ''}`}
                        style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        data-testid={`schedule-day-header-${iso}`}>
                        <div>{fmtDayShort(iso)}</div>
                        <div className="text-slate-500">{fmtDayNum(iso)}</div>
                        {wf && <WeatherBadge forecast={wf} />}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.contractors.length === 0 && (
                  <tr>
                    <td colSpan={visibleDays.length + 1} className="p-8 text-center text-sm text-slate-500">
                      No contractors seeded. Add contractors in Address Book to start booking.
                    </td>
                  </tr>
                )}
                {data.contractors.map((c) => {
                  const overbookedDays = overbookedByContractor[c._id] || 0;
                  const rowEvents = eventsByContractor[c._id] || [];
                  return (
                    <tr key={c._id}>
                      <td className="border border-slate-300 px-3 py-2 sticky left-0 bg-white z-10">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-bold text-slate-900 flex-1">{c.name}</div>
                          {overbookedDays > 0 && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-rose-600 text-white px-1.5 py-0.5"
                              title={`${overbookedDays} day${overbookedDays === 1 ? '' : 's'} in this window have ≥ 2 overlapping events`}
                              data-testid={`schedule-overbook-pill-${c._id}`}>
                              OVERBOOK · {overbookedDays}
                            </span>
                          )}
                          <button
                            onClick={() => setAvailabilityFor(c)}
                            data-testid={`schedule-availability-btn-${c._id}`}
                            title="Edit weekly availability"
                            className="text-[9px] uppercase tracking-wider border border-slate-400 px-1.5 py-0.5 hover:bg-slate-100">
                            Avail
                          </button>
                        </div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{c.trade || '—'}</div>
                      </td>
                      {visibleDays.map((iso) => {
                        const overlaps = rowEvents.filter(e =>
                          e.start_date <= iso && (e.end_date || e.start_date) >= iso
                        ).length;
                        const isOverbooked = overlaps >= 2;
                        const weekBoundary = (new Date(iso + 'T00:00:00').getDay() === 1);
                        // Chip payloads for this cell (session-level for 2C)
                        const chips = buildCellChips(rowEvents, iso, view, reconMap);
                        return (
                          <td key={iso}
                            onDragOver={onDragOverCell}
                            onDrop={onDropCell(c._id, iso)}
                            className={`border border-slate-300 align-top p-1 h-16 hover:bg-emerald-50/50 ${weekBoundary ? 'border-l-2 border-l-slate-500' : ''} ${isWeekend(iso) ? 'bg-slate-100' : ''} ${isOverbooked ? 'bg-rose-50' : ''}`}
                            data-testid={`schedule-cell-${c._id}-${iso}`}>
                            {chips.map((chip, idx) => (
                              <SessionChip key={`${chip.event._id}-${chip.session?.session_no || 'evt'}-${idx}`}
                                chip={chip} view={view}
                                onDragStart={onDragStartFromGrid(chip.event)}
                                onClick={() => setSelectedEvent(chip.event)}
                              />
                            ))}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Legend footer ─────────────────────────────────────────── */}
      <footer className="fixed bottom-0 left-0 right-0 bg-slate-900 text-slate-300 px-6 py-2 flex items-center gap-6 text-[10px] uppercase tracking-wider border-t-2 border-slate-700 z-20">
        {Object.entries(EVENT_TYPE_COLOUR).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 border-l-4 ${v.br} ${v.bg}`} />
            {k}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 border-l-4 border-dashed border-violet-500 bg-violet-100" />
          cure-gap
        </span>
        <span className="ml-auto">
          View: <span className="text-amber-300 font-bold">{view}</span> ·
          Window: <span className="text-emerald-300 font-bold">{windowKey}</span>
        </span>
      </footer>

      {/* ── Availability drawer (Phase 2E) ───────────────────────── */}
      {availabilityFor && (
        <AvailabilityPickerDrawer
          api={api}
          contractor={availabilityFor}
          defaultWeekStart={weekStart}
          onClose={() => setAvailabilityFor(null)}
        />
      )}

      {/* ── Detail drawer ─────────────────────────────────────────── */}
      {selectedEvent && (
        <EventDrawer
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onPatch={updateSelectedEvent}
          onReplaceSessions={replaceSelectedSessions}
          onDelete={deleteSelectedEvent}
          reconciliation={reconMap[selectedEvent._id]}
          view={view}
        />
      )}

      <div className="h-10" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Chip builder — one row per session that intersects `dayIso`
// ─────────────────────────────────────────────────────────────
function buildCellChips(rowEvents, dayIso, view, reconMap) {
  const chips = [];
  for (const ev of rowEvents) {
    const sessions = Array.isArray(ev.sessions) && ev.sessions.length > 0
      ? ev.sessions
      : null;
    if (sessions) {
      for (const s of sessions) {
        const sStart = (s.start_date || '').slice(0, 10);
        const sEnd = (s.end_date || sStart).slice(0, 10);
        if (dateInRange(dayIso, sStart, sEnd)) {
          chips.push({ event: ev, session: s, startsHere: dayIso === sStart });
        }
      }
    } else {
      // Legacy event with no sessions[] — one big chip
      const eStart = ev.start_date;
      const eEnd = ev.end_date || ev.start_date;
      if (dateInRange(dayIso, eStart, eEnd)) {
        chips.push({ event: ev, session: null, startsHere: dayIso === eStart });
      }
    }
  }
  // De-dupe: only render chip on its FIRST day (visual "start"), unless
  // view=actual/reconciled where we want per-day dots too. Keep per-day
  // for now — Trevor gets to see the whole span coloured.
  return chips;
}

function SessionChip({ chip, view, onDragStart, onClick }) {
  const { event: ev, session, startsHere } = chip;
  const tk = EVENT_TYPE_COLOUR[ev.event_type] || EVENT_TYPE_COLOUR.other;
  const IconEl = tk.icon;
  const isCureGap = session?.role === 'cure_gap';
  const roleStyle = isCureGap ? SESSION_ROLE_STYLE.cure_gap : null;
  const statusTint = session?.status ? SESSION_STATUS_TINT[session.status] : '';

  // Only show the FULL details on the starting day; on continuation
  // days show a compact stripe to save vertical space.
  const compact = !startsHere;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className={`border-l-4 border ${
        roleStyle ? roleStyle.br + ' ' + roleStyle.bg + ' ' + roleStyle.fg
                  : tk.br + ' ' + tk.bg + ' ' + tk.fg
      } ${roleStyle?.dashed ? 'border-dashed' : ''} ${STATUS_ACCENT[ev.status] || ''} ${statusTint || ''} p-1 text-[10px] cursor-pointer hover:brightness-95 mb-1`}
      data-testid={`schedule-event-${ev._id}${session ? '-s' + session.session_no : ''}`}
      title={session ? `#${session.session_no} · ${session.role}${session.status ? ` · ${session.status}` : ''}` : undefined}
    >
      {!compact && (
        <>
          <div className="flex items-center gap-1 mb-0.5">
            <IconEl size={10} weight="bold" />
            <span className="font-bold truncate text-[10px]">
              A{ev.appraisal_no || (ev.appraisal_id || '').slice(-4)}
            </span>
            {ev.weather_dependent && (
              <CloudRain size={9} weight="bold" className="ml-auto text-sky-600" title="Weather-dependent" />
            )}
          </div>
          <div className="truncate text-[9px] opacity-80">
            {ev.client_name || ev.quote_type}
          </div>
          {session && (
            <div className="text-[9px] mt-0.5 uppercase tracking-wider font-mono">
              #{session.session_no} · {session.role}
              {isCureGap && session.cure_reason && ` · ${session.cure_reason.replace(/_/g, ' ')}`}
            </div>
          )}
          {!session && (
            <div className="text-[9px] mt-0.5 uppercase tracking-wider font-mono opacity-70">
              {ev.event_type} · {ev.estimated_hours}h · c{ev.crew_size}
            </div>
          )}
          {(view === 'reconciled' || view === 'overlay') && (
            <ReconciledBadge chip={chip} />
          )}
        </>
      )}
      {compact && (
        <div className="text-[9px] font-mono opacity-70 text-center">
          {session ? `#${session.session_no}` : '···'}
        </div>
      )}
    </div>
  );
}

function ReconciledBadge({ chip }) {
  // Rendered on Reconciled/Overlay chips — surfaces per-session variance
  // if any log exists at all. This is a small info marker; the drawer
  // holds the full reconciliation table.
  return (
    <div className="mt-1 text-[9px] font-mono border-t border-slate-300 pt-0.5 opacity-70">
      Δ hrs pending
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Detail drawer with Sessions editor
// ─────────────────────────────────────────────────────────────
function EventDrawer({ event, onClose, onPatch, onReplaceSessions, onDelete, reconciliation, view }) {
  const [draftSessions, setDraftSessions] = useState(() =>
    Array.isArray(event.sessions) ? JSON.parse(JSON.stringify(event.sessions)) : []
  );
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showReconOnly, setShowReconOnly] = useState(view === 'reconciled' || view === 'overlay');

  useEffect(() => {
    setDraftSessions(Array.isArray(event.sessions) ? JSON.parse(JSON.stringify(event.sessions)) : []);
    setDirty(false);
  }, [event._id, event.plan_version]);

  const patchSession = (idx, patch) => {
    const next = draftSessions.map((s, i) => i === idx ? { ...s, ...patch } : s);
    setDraftSessions(next);
    setDirty(true);
  };
  const removeSession = (idx) => {
    setDraftSessions(draftSessions.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const addSession = () => {
    setDraftSessions([
      ...draftSessions,
      {
        session_no: draftSessions.length + 1,
        role: 'install',
        required_skill: null,
        start_date: event.start_date,
        end_date: event.start_date,
        estimated_hours: 4,
        estimated_quantity: 0,
        quantity_unit: '',
        work_rate_key: '',
        cure_reason: null,
        cure_acceleration_options: [],
        status: 'pending',
      },
    ]);
    setDirty(true);
  };
  const commitSessions = async () => {
    setSaving(true);
    // Re-index session_no so 1..n contiguous
    const cleaned = draftSessions.map((s, i) => ({
      ...s,
      session_no: i + 1,
      cure_reason: s.role === 'cure_gap' ? (s.cure_reason || 'other') : null,
    }));
    await onReplaceSessions(cleaned);
    setSaving(false);
    setDirty(false);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-end"
         onClick={onClose}>
      <div className="w-[28rem] h-full bg-white border-l-2 border-slate-800 p-5 overflow-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="schedule-event-drawer">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-slate-700"
              style={{ fontFamily: "'Chivo', sans-serif" }}>
            Event Detail
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"
                  data-testid="schedule-drawer-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <FieldRow label="Appraisal">
            A{event.appraisal_no || event.appraisal_id?.slice(-4)}
          </FieldRow>
          <FieldRow label="Client">{event.client_name || '—'}</FieldRow>
          <FieldRow label="Plan version">
            v{event.plan_version || 1}
          </FieldRow>

          <FieldSelect label="Event Type" value={event.event_type}
            options={Object.keys(EVENT_TYPE_COLOUR)}
            onChange={(v) => onPatch({ event_type: v })}
            testid="schedule-drawer-type" />
          <FieldSelect label="Status" value={event.status}
            options={['tentative', 'confirmed', 'completed', 'cancelled']}
            onChange={(v) => onPatch({ status: v })}
            testid="schedule-drawer-status" />
          <FieldInput label="Start Date" type="date" value={event.start_date}
            onChange={(v) => onPatch({ start_date: v })}
            testid="schedule-drawer-start" />
          <FieldInput label="End Date" type="date" value={event.end_date}
            onChange={(v) => onPatch({ end_date: v })}
            testid="schedule-drawer-end" />
          <FieldInput label="Estimated Hours" type="number" step="0.5" min="0"
            value={event.estimated_hours}
            onChange={(v) => onPatch({ estimated_hours: parseFloat(v) || 0 })}
            testid="schedule-drawer-hours" />
          <FieldInput label="Crew Size" type="number" min="1" value={event.crew_size}
            onChange={(v) => onPatch({ crew_size: parseInt(v) || 1 })}
            testid="schedule-drawer-crew" />
          <FieldCheckbox label="Weather Dependent"
            checked={!!event.weather_dependent}
            onChange={(v) => onPatch({ weather_dependent: v })}
            testid="schedule-drawer-weather" />

          <FieldTextArea label="Notes" value={event.notes || ''}
            onChange={(v) => onPatch({ notes: v })}
            testid="schedule-drawer-notes" />
        </div>

        {/* ── Sessions editor (Phase 2C) ─────────────────────── */}
        <div className="mt-6 pt-4 border-t border-slate-300">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
              Sessions · {draftSessions.length}
            </h4>
            <button onClick={addSession}
              className="text-[10px] uppercase tracking-wider bg-slate-800 text-white px-2 py-1 hover:bg-slate-700 inline-flex items-center gap-1"
              data-testid="schedule-drawer-add-session">
              <Plus size={10} weight="bold" /> Add
            </button>
          </div>
          <div className="space-y-2" data-testid="schedule-drawer-sessions-list">
            {draftSessions.length === 0 && (
              <div className="text-[11px] text-slate-500 italic border border-dashed border-slate-300 p-2">
                No sessions on this event. Add one above to enable per-session logging.
              </div>
            )}
            {draftSessions.map((s, idx) => (
              <div key={idx}
                   className={`border-l-4 p-2 text-[11px] ${
                     s.role === 'cure_gap'
                       ? 'border-l-violet-500 bg-violet-50 border-dashed border-violet-300'
                       : 'border-l-slate-600 bg-slate-50 border border-slate-200'
                   }`}
                   data-testid={`schedule-drawer-session-${idx}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono font-bold uppercase">#{idx + 1}</span>
                  <button onClick={() => removeSession(idx)}
                          className="text-rose-600 hover:text-rose-800"
                          data-testid={`schedule-drawer-remove-session-${idx}`}>
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Role</span>
                    <select value={s.role}
                            onChange={(e) => patchSession(idx, { role: e.target.value })}
                            className="border border-slate-300 px-1 py-0.5 text-[11px]"
                            data-testid={`schedule-drawer-session-role-${idx}`}>
                      {SESSION_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </label>
                  {s.role === 'cure_gap' && (
                    <label className="flex flex-col">
                      <span className="text-[9px] uppercase tracking-wider text-slate-500">Cure reason</span>
                      <select value={s.cure_reason || 'other'}
                              onChange={(e) => patchSession(idx, { cure_reason: e.target.value })}
                              className="border border-slate-300 px-1 py-0.5 text-[11px]"
                              data-testid={`schedule-drawer-session-cure-${idx}`}>
                        {CURE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Start</span>
                    <input type="date" value={(s.start_date || '').slice(0, 10)}
                           onChange={(e) => patchSession(idx, { start_date: e.target.value })}
                           className="border border-slate-300 px-1 py-0.5 text-[11px]" />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">End</span>
                    <input type="date" value={(s.end_date || '').slice(0, 10)}
                           onChange={(e) => patchSession(idx, { end_date: e.target.value })}
                           className="border border-slate-300 px-1 py-0.5 text-[11px]" />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Est. hours</span>
                    <input type="number" step="0.25" min="0"
                           value={s.estimated_hours || ''}
                           onChange={(e) => patchSession(idx, { estimated_hours: parseFloat(e.target.value) || 0 })}
                           className="border border-slate-300 px-1 py-0.5 text-[11px] font-mono" />
                  </label>
                  <label className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Est. quantity</span>
                    <input type="number" step="0.1" min="0"
                           value={s.estimated_quantity || ''}
                           onChange={(e) => patchSession(idx, { estimated_quantity: parseFloat(e.target.value) || 0 })}
                           className="border border-slate-300 px-1 py-0.5 text-[11px] font-mono" />
                  </label>
                  <label className="flex flex-col col-span-2">
                    <span className="text-[9px] uppercase tracking-wider text-slate-500">Work rate key</span>
                    <input type="text" value={s.work_rate_key || ''}
                           onChange={(e) => patchSession(idx, { work_rate_key: e.target.value })}
                           placeholder="e.g. concrete_bedding"
                           className="border border-slate-300 px-1 py-0.5 text-[11px] font-mono" />
                  </label>
                </div>
                <div className="text-[9px] font-mono text-slate-500 mt-1">
                  status: {s.status || 'pending'}
                </div>
              </div>
            ))}
          </div>
          {dirty && (
            <button onClick={commitSessions} disabled={saving}
              className="mt-3 w-full bg-slate-900 text-white text-xs uppercase tracking-wider py-2 hover:bg-slate-700 disabled:opacity-50"
              data-testid="schedule-drawer-save-sessions">
              {saving ? 'Saving…' : `Save sessions · bumps plan_version`}
            </button>
          )}
        </div>

        {/* ── Reconciliation summary ─────────────────────────── */}
        {reconciliation && (
          <div className="mt-6 pt-4 border-t border-slate-300"
               data-testid="schedule-drawer-reconciliation">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
                Reconciliation · v{reconciliation.plan_version_current}
              </h4>
              {showReconOnly ? (
                <span className="text-[9px] uppercase tracking-wider text-amber-700">live</span>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <MetricCell label="Planned hrs" value={reconciliation.totals.planned_hours} />
              <MetricCell label="Actual hrs" value={reconciliation.totals.actual_hours}
                          rose={reconciliation.totals.actual_hours > reconciliation.totals.planned_hours} />
              <MetricCell label="Planned qty" value={reconciliation.totals.planned_quantity} />
              <MetricCell label="Actual qty" value={reconciliation.totals.actual_quantity} />
              <MetricCell label="Sessions logged" value={reconciliation.totals.sessions_logged} />
              <MetricCell label="Sessions pending" value={reconciliation.totals.sessions_pending}
                          amber={reconciliation.totals.sessions_pending > 0} />
            </div>
            {reconciliation.ad_hoc_count > 0 && (
              <div className="mt-2 text-[10px] text-amber-800 uppercase tracking-wider">
                {reconciliation.ad_hoc_count} ad-hoc log{reconciliation.ad_hoc_count === 1 ? '' : 's'}
              </div>
            )}
            <ul className="mt-2 space-y-1">
              {reconciliation.per_session_variance.map((p) => (
                <li key={p.session_no}
                    className="text-[10px] font-mono flex items-center gap-2 border-l-2 border-slate-300 px-2 py-0.5">
                  <span className="font-bold">#{p.session_no}</span>
                  <span className="text-slate-600">{p.role}</span>
                  <span className={`ml-auto ${p.hours_delta > 0 ? 'text-rose-700' : p.hours_delta < 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                    Δhrs {p.hours_delta > 0 ? '+' : ''}{p.hours_delta}
                  </span>
                  <span className="text-slate-500">
                    · {p.logs_count} log{p.logs_count === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-slate-300 flex gap-2">
          <button onClick={onDelete}
            className="flex-1 bg-rose-600 text-white text-xs uppercase tracking-wider py-2 hover:bg-rose-700"
            data-testid="schedule-drawer-delete">
            Delete Event
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricCell({ label, value, rose, amber }) {
  return (
    <div className={`border-l-4 ${rose ? 'border-rose-500 bg-rose-50' : amber ? 'border-amber-500 bg-amber-50' : 'border-slate-400 bg-slate-50'} px-2 py-1`}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[13px] font-bold text-slate-900">{value ?? 0}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Small form primitives
// ─────────────────────────────────────────────────────────────
function FieldRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className="text-sm text-slate-900" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
        {children}
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = 'text', testid, ...rest }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <input
        type={type}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (local !== value) onChange(local); }}
        className="w-full border border-slate-300 px-2 py-1 text-sm"
        data-testid={testid}
        {...rest}
      />
    </div>
  );
}

function FieldTextArea({ label, value, onChange, testid }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => setLocal(value ?? ''), [value]);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <textarea
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (local !== value) onChange(local); }}
        rows={2}
        className="w-full border border-slate-300 px-2 py-1 text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function FieldSelect({ label, value, options, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 px-2 py-1 text-sm bg-white"
        data-testid={testid}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function FieldCheckbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <span className="text-sm text-slate-800">{label}</span>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// Weather badge (Phase 2E · mocked forecast strip)
// ─────────────────────────────────────────────────────────────
const WEATHER_ICON = {
  clear: Sun, sunny_dry: Sun,
  cloudy: Cloud,
  rain: CloudRain, wet: CloudRain,
  wind: Wind, windy: Wind, storm: Wind,
  cold: Snowflake,
  hot: Thermometer,
  unknown: null,
};
const WEATHER_TINT = {
  rain: 'bg-sky-200 text-sky-900',
  wet: 'bg-sky-200 text-sky-900',
  storm: 'bg-rose-200 text-rose-900',
  wind: 'bg-amber-100 text-amber-900',
  windy: 'bg-amber-100 text-amber-900',
  cold: 'bg-slate-200 text-slate-900',
  hot: 'bg-orange-200 text-orange-900',
  clear: 'bg-yellow-100 text-yellow-800',
  sunny_dry: 'bg-yellow-100 text-yellow-800',
  cloudy: 'bg-slate-100 text-slate-700',
  unknown: '',
};

function WeatherBadge({ forecast }) {
  const cond = (forecast?.condition || 'unknown').toLowerCase();
  const Icon = WEATHER_ICON[cond];
  const tint = WEATHER_TINT[cond] || '';
  if (!Icon) return null;
  return (
    <div
      className={`mt-1 mx-auto w-fit px-1 py-0.5 flex items-center gap-0.5 text-[9px] font-mono ${tint}`}
      title={`${cond}${forecast.temp_c ? ` · ${forecast.temp_c}°C` : ''}${forecast.wind_kph ? ` · ${forecast.wind_kph}km/h` : ''}${forecast.workable === false ? ' · NOT WORKABLE' : ''}`}
      data-testid={`schedule-weather-badge-${forecast.date}`}
    >
      <Icon size={9} weight="bold" />
      <span className="uppercase tracking-wider">{cond.replace('_', ' ')}</span>
    </div>
  );
}
